/**
 * Formatting Utilities
 * Provides formatting functions for dates, numbers, and other display values
 */

/**
 * Format date string to locale format
 */
export function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

/**
 * Format date to short format (MM/DD/YYYY)
 */
export function formatDateShort(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
}

/**
 * Format date for input field (YYYY-MM-DD)
 */
export function formatDateForInput(dateString: string): string {
  const date = new Date(dateString);
  const isoString = date.toISOString().split('T')[0];
  return isoString || '';
}

/**
 * Format number with commas
 */
export function formatNumber(num: number, decimals: number = 0): string {
  return num.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

/**
 * Format percentage
 */
export function formatPercentage(value: number, decimals: number = 1): string {
  return `${value.toFixed(decimals)}%`;
}

/**
 * Format weight in tons
 */
export function formatWeight(tons: number): string {
  return `${formatNumber(tons, 2)} tons`;
}

/**
 * Format phone number
 */
export function formatPhoneNumber(phone: string | undefined): string {
  if (!phone) return '';

  const cleaned = phone.replace(/\D/g, '');

  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  }

  return phone;
}

/**
 * Format waste stream type to display name
 */
export function formatWasteStreamType(type: string): string {
  const typeMap: Record<string, string> = {
    wood: 'Wood',
    metal: 'Metal',
    concrete: 'Concrete',
    asphalt: 'Asphalt',
    drywall: 'Drywall',
    cardboard: 'Cardboard',
    plastic: 'Plastic',
    glass: 'Glass',
    carpet: 'Carpet',
    other: 'Other',
  };

  return typeMap[type] || type;
}

/**
 * Format diversion method to display name
 */
export function formatDiversionMethod(method: string): string {
  const methodMap: Record<string, string> = {
    recycle: 'Recycle',
    reuse: 'Reuse',
    salvage: 'Salvage',
    donate: 'Donate',
    landfill: 'Landfill',
  };

  return methodMap[method] || method;
}

/**
 * Format project type to display name
 */
export function formatProjectType(type: string): string {
  const typeMap: Record<string, string> = {
    new_construction: 'New Construction',
    renovation: 'Renovation',
    demolition: 'Demolition',
    mixed: 'Mixed',
  };

  return typeMap[type] || type;
}

/**
 * Format plan status to display name
 */
export function formatPlanStatus(status: string): string {
  const statusMap: Record<string, string> = {
    draft: 'Draft',
    submitted: 'Submitted',
    approved: 'Approved',
    rejected: 'Rejected',
  };

  return statusMap[status] || status;
}

/**
 * Get status color class
 */
export function getStatusColor(status: string): string {
  const colorMap: Record<string, string> = {
    draft: 'text-neutral-600 bg-neutral-100',
    submitted: 'text-primary-700 bg-primary-100',
    approved: 'text-accent-700 bg-accent-100',
    rejected: 'text-red-700 bg-red-100',
  };

  return colorMap[status] || 'text-neutral-600 bg-neutral-100';
}

/**
 * Format bid status to display name
 */
export function formatBidStatus(status: string): string {
  const statusMap: Record<string, string> = {
    pending: 'Pending',
    accepted: 'Accepted',
    rejected: 'Rejected',
    withdrawn: 'Withdrawn',
    awarded: 'Awarded',
  };

  return statusMap[status] || status;
}

/**
 * Get bid status color class
 */
export function getBidStatusColor(status: string): string {
  const colorMap: Record<string, string> = {
    pending: 'text-yellow-700 bg-yellow-100',
    accepted: 'text-accent-700 bg-accent-100',
    rejected: 'text-red-700 bg-red-100',
    withdrawn: 'text-neutral-700 bg-neutral-100',
    awarded: 'text-green-700 bg-green-100',
  };

  return colorMap[status] || 'text-neutral-600 bg-neutral-100';
}

/**
 * Format currency value
 */
export function formatCurrency(amount: number | null | undefined): string {
  if (amount === null || amount === undefined) return 'N/A';

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

/**
 * Format job status to display name
 */
export function formatJobStatus(status: string): string {
  const statusMap: Record<string, string> = {
    active: 'Open',
    in_progress: 'In Progress',
    complete: 'Completed',
  };

  return statusMap[status] || status;
}

/**
 * Get job status color class
 */
export function getJobStatusColor(status: string): string {
  const colorMap: Record<string, string> = {
    active: 'text-accent-700 bg-accent-100',
    in_progress: 'text-primary-700 bg-primary-100',
    complete: 'text-neutral-700 bg-neutral-100',
  };

  return colorMap[status] || 'text-neutral-600 bg-neutral-100';
}

/**
 * Format Unix timestamp to short date
 */
export function formatTimestamp(timestamp: number): string {
  const date = new Date(timestamp * 1000);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
}

/**
 * Calculate days between deployment date and now
 */
export function calculateDaysOnSite(deploymentDate: string): number {
  const deployed = new Date(deploymentDate);
  const now = new Date();
  const diffTime = Math.abs(now.getTime() - deployed.getTime());
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays;
}

/**
 * Format deployment date to abbreviated format (e.g., "Oct 18, 2025")
 */
export function formatDeploymentDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/**
 * Get badge variant based on days on site
 * Green: 0-7 days, Yellow: 8-14 days, Orange: 15-30 days, Red: 30+ days
 */
export function getDaysOnSiteBadgeVariant(
  days: number
): 'success' | 'warning' | 'destructive' | 'secondary' {
  if (days <= 7) return 'success';
  if (days <= 14) return 'secondary';
  if (days <= 30) return 'warning';
  return 'destructive';
}